import { Resizable } from 're-resizable';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { SyncAreaSize } from 'packages/lib/common-ui/sync-area-size/sync-area-size';
import {
  ToggleSiderOptions,
  atShellPublicMethodsAtom,
  elementSizeAtomFam,
} from 'packages/lib/common-ui/state';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  appHeaderHeight,
  layoutId,
  siderAreaId,
  siderHeaderTitleId,
  globalFooterAreaId,
} from 'packages/at-shell/components/app-shell/app-layout-constants';
import { Typography } from 'antd';
import { AppShellConfigRequired } from 'packages/at-shell/components/app-shell/app-layout';

const Wrapper = styled.div<{
  hasSiderTitle: boolean;
  globalFooterHeight: number;
  config: AppShellConfigRequired;
}>`
  position: absolute;
  height: ${({ hasSiderTitle, globalFooterHeight }): string =>
    hasSiderTitle
      ? `calc(100vh - ${globalFooterHeight}px)`
      : `calc(100vh - ${appHeaderHeight + globalFooterHeight}px)`};
  top: ${({ hasSiderTitle }): string =>
    hasSiderTitle ? '0' : `${appHeaderHeight}px`};
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 100;
  overflow-x: hidden;

  .resizable {
    height: 100%;

    .header {
      height: ${({ hasSiderTitle }): string =>
        hasSiderTitle ? `${appHeaderHeight}px` : '0'};
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0.25rem 1rem 0.25rem 0.5rem;
      text-overflow: ellipsis;

      .anticon-close {
        font-size: 1rem;
      }

      #${siderHeaderTitleId} {
        white-space: nowrap;
        width: 100%;
      }
    }

    .footer {
      display: flex;
      justify-content: space-between;
      position: relative;
      z-index: 1;
      align-items: center;
      padding: 0.5rem;
      width: 100%;
      overflow: hidden;

      button:not(:last-child) {
        margin-right: 0.25rem;
      }
    }

    .sider {
      position: relative;
      height: 100%;
      min-width: ${({ config }): string => `${config.siderMinWidth}px`};
      transition: all 0.3s ease-in-out;
      display: flex;
      flex-flow: column wrap;
      justify-content: space-between;
      container-type: inline-size;

      .siderContent {
        flex: 1;
        width: 100%;
        min-width: ${({ config }): string => `${config.siderMinWidth}px`};
        overflow-y: auto;
      }

      @container (max-width: 240px) {
        .siderContent {
          display: none;
        }
        .footer {
          display: none;
        }
        .footer {
          display: none;
        }
        #${siderHeaderTitleId} {
          display: none;
        }
      }

      &::before {
        display: ${({ config }) => (!config.siderResizable ? 'none' : 'auto')};
        content: '';
        width: 2px;
        height: 2rem;
        background-color: #ccc;
        border-radius: 4px;
        position: absolute;
        top: calc(50% - 2rem);
        left: -2px;
      }
    }
  }
`;

const DEFAULT_ENABLE = {
  top: false,
  right: false,
  bottom: false,
  left: true,
  topRight: false,
  bottomRight: false,
  bottomLeft: false,
  topLeft: false,
};

const Header: FunctionComponent<
  PropsWithChildren<{
    config: AppShellConfigRequired;
    resizableRef: RefObject<Resizable>;
    visible: boolean;
  }>
> = ({ children, config, resizableRef, visible }) => {
  const prevWidthRef = useRef(config.siderMinWidth);
  const siderWidth = useRecoilValue(elementSizeAtomFam(siderAreaId));
  const setAtShellPublicMethods = useSetRecoilState(atShellPublicMethodsAtom);

  const onClose = (): void => {
    const el = resizableRef.current?.resizable;
    const w = siderWidth?.width;
    if (el?.style.width && w) {
      prevWidthRef.current = w;
      el.style.width = `${config.siderMinWidth}px`;
    }
  };

  const onOpen = (): void => {
    const el = resizableRef.current?.resizable;
    if (el && el.style.width) {
      const w =
        prevWidthRef.current === config.siderMinWidth
          ? config.defaultSiderOpenWidth
          : prevWidthRef.current;
      el.style.width = `${w}px`;
    }
  };

  /** Method exposed to app shell apps. Used by Classification Enrichment tool to expand/collapse sider to majority or minority of main view width. */
  const toggleSider = useCallback(
    ({
      threshold,
      expandedWidth,
      collapsedWidth,
    }: ToggleSiderOptions): void => {
      const shouldExpand =
        Math.floor(siderWidth?.width ?? 0) < Math.floor(threshold);

      const el = resizableRef.current?.resizable;
      if (el && el.style.width) {
        el.style.width = `${shouldExpand ? expandedWidth : collapsedWidth}px`;
      }
    },
    [siderWidth, resizableRef]
  );

  const toggleSiderVisibility = useCallback((): void => {
    const el = resizableRef.current?.resizable;
    if (!el || !siderWidth) {
      return;
    }
    const isVisible = siderWidth.width > 0;
    if (isVisible) {
      prevWidthRef.current = siderWidth.width;
      el.style.width = '0px';
    } else {
      el.style.width = `${prevWidthRef.current}px`;
    }
  }, [siderWidth, resizableRef]);

  useEffect(() => {
    setAtShellPublicMethods({ toggleSider, toggleSiderVisibility });
    return () => {
      setAtShellPublicMethods({});
    };
  }, [toggleSider, toggleSiderVisibility, setAtShellPublicMethods]);

  if (!visible) {
    return null;
  }

  if ((siderWidth?.width ?? 0) <= config.siderMinWidth) {
    return (
      <div className="header">
        {children}
        <MenuFoldOutlined
          style={{ marginLeft: '1.5rem', color: 'white' }}
          onClick={onOpen}
        />
      </div>
    );
  }
  return (
    <div className="header">
      {children}
      <MenuUnfoldOutlined
        style={{ marginLeft: '1.5rem', color: 'white' }}
        onClick={onClose}
      />
    </div>
  );
};

const Footer: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  return <div className="footer">{children}</div>;
};

export const AppSider: FunctionComponent<
  PropsWithChildren<{
    config: AppShellConfigRequired;
    footerActions?: ReactNode;
  }>
> = ({ config, children, footerActions }) => {
  const globalFooterBbox = useRecoilValue(
    elementSizeAtomFam(globalFooterAreaId)
  );
  const layoutBbox = useRecoilValue(elementSizeAtomFam(layoutId));
  const resizableRef = useRef<Resizable>(null);
  const minWidthIsVW = config.primaryViewMinWidth.endsWith('vw');
  const minWidthInt = Number.parseInt(config.primaryViewMinWidth);
  // Since the sider doesn't respect a minWidth set on the primaryView, we must instead do the inverse:
  //   compute the maxWidth for the sider based on the inverse of the minWidth for the primary view
  const maxSiderWidth =
    !layoutBbox || minWidthIsVW
      ? `${100 - minWidthInt}vw`
      : `${layoutBbox.width - minWidthInt}px`;
  useLayoutEffect(() => {
    if (!children) {
      const el = resizableRef.current?.resizable;
      if (el && el.style.width) {
        el.style.width = `0px`;
      }
    }
  }, [children]);
  return (
    <Wrapper
      hasSiderTitle={config.siderTitle !== false}
      globalFooterHeight={globalFooterBbox?.height ?? 0}
      config={config}
    >
      <Resizable
        ref={resizableRef}
        maxWidth={maxSiderWidth}
        maxHeight="100vh"
        minWidth={`${config.siderMinWidth}px`}
        className="resizable"
        defaultSize={{
          width: config.defaultIsSiderOpen
            ? config.defaultSiderOpenWidth
            : config.siderMinWidth,
          height: '100%',
        }}
        enable={{
          ...DEFAULT_ENABLE,
          left: config.siderResizable ?? DEFAULT_ENABLE.left,
        }}
      >
        <SyncAreaSize
          id={siderAreaId}
          elementSizeAtomFam={elementSizeAtomFam}
        />
        <div className="sider">
          <Header
            visible={config.siderTitle !== false}
            config={config}
            resizableRef={resizableRef}
          >
            <Typography.Title
              id={siderHeaderTitleId}
              style={{ margin: 0 }}
              level={4}
            >
              {config.siderTitle}
            </Typography.Title>
          </Header>
          <div className="siderContent">{children}</div>
          {footerActions ? <Footer>{footerActions}</Footer> : null}
        </div>
      </Resizable>
    </Wrapper>
  );
};
